import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Play,
  X,
  Settings,
  Volume2,
  VolumeX,
  Maximize,
  Film,
} from "lucide-react";

const PlayerPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const content = location.state?.content;

  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  // Auto-hide controls after 3s when playing
  useEffect(() => {
    let timer;
    if (showControls && isPlaying) {
      timer = setTimeout(() => setShowControls(false), 3000);
    }
    return () => clearTimeout(timer);
  }, [showControls, isPlaying]);
 useEffect(() => {
  try {
    const adEl = document.querySelector(".adsbygoogle");
    // Only push if the slot hasn't been filled yet
    if (adEl && !adEl.getAttribute("data-adsbygoogle-status")) {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    }
  } catch (e) {
    console.error("AdSense error:", e);
  }
}, []);

  // Sync mute state with video element
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  // Update progress bar as video plays
  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (video && video.duration) {
      setProgress((video.currentTime / video.duration) * 100);
    }
  };

  const handleBack = () => {
    if (videoRef.current) videoRef.current.pause();
    navigate(-1);
  };

  const handlePlayPause = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
    } else {
      video.play().catch((err) => console.error("Play failed:", err));
    }
    
  };

  const handleProgressClick = (e) => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    video.currentTime = percentage * video.duration;
    setProgress(percentage * 100);
  };

  const handleSkip = (seconds) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, Math.min(video.duration, video.currentTime + seconds));
  };

  const handleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.requestFullscreen) video.requestFullscreen();
    else if (video.webkitRequestFullscreen) video.webkitRequestFullscreen();
  };

  if (!content) {
    return (
      <div className="player-container">
        <div className="video-wrapper">
          <div className="video-placeholder">
            <Film size={80} style={{ marginBottom: "20px", color: "var(--accent-gold)" }} />
            <div style={{ fontSize: "18px", color: "var(--text-tertiary)" }}>
              No content selected
            </div>
            <button
              className="btn-primary"
              style={{ marginTop: "24px" }}
              onClick={() => navigate("/")}
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const videoUrl = content.cloudinaryUrl;

  return (
    <div className="player-container">
      <div
        className="video-wrapper"
        onClick={() => setShowControls(!showControls)}
        style={{ position: "relative", background: "#000", width: "100%", height: "100svh" }}
      >
        {/* ── CLOUDINARY VIDEO ── */}
        {videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl}
            onTimeUpdate={handleTimeUpdate}
            onEnded={() => setIsPlaying(false)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            playsInline        
            preload="auto"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
              background: "#000",
            }}
          />
        ) : (
          // Fallback if no URL is set yet
          <div className="video-placeholder">
            <Film size={80} style={{ marginBottom: "20px", color: "var(--accent-gold)" }} />
            <div style={{ fontSize: "20px", fontWeight: "700", fontFamily: "'Playfair Display', serif" }}>
              {content.title}
            </div>
            <div style={{ fontSize: "14px", marginTop: "8px", color: "var(--text-tertiary)" }}>
              Video not available yet
            </div>
          </div>
        )}

        {/* ── CONTROLS OVERLAY ── */}
        <div
          className={`player-controls ${showControls ? "visible" : ""}`}
          style={{ position: "absolute", inset: 0, pointerEvents: showControls ? "auto" : "none" }}
        >
          {/* Header */}
          <div className="player-header">
            <button className="icon-btn" onClick={handleBack}>
              <X size={24} />
            </button>
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: "14px", fontWeight: "700", fontFamily: "'Playfair Display', serif", color: "#fff" }}>
                {content.title}
              </div>
              <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.6)", marginTop: 2 }}>
                {content.originalYear} · {content.country}
              </div>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                className="icon-btn"
                onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }}
              >
                {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
              </button>
              <button
                className="icon-btn"
                onClick={(e) => { e.stopPropagation(); handleFullscreen(); }}
              >
                <Maximize size={20} />
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="player-footer">
            <div className="progress-bar" onClick={handleProgressClick}>
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>

            <div className="playback-controls">
              {/* Skip back 10s */}
              <button className="icon-btn" onClick={(e) => { e.stopPropagation(); handleSkip(-10); }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11 17V7L5.5 12L11 17ZM13 7V17L18.5 12L13 7Z" />
                </svg>
              </button>

              {/* Play / Pause */}
              <button className="play-pause-btn" onClick={handlePlayPause}>
                {isPlaying ? (
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 4H10V20H6V4ZM14 4H18V20H14V4Z" />
                  </svg>
                ) : (
                  <Play size={30} fill="currentColor" />
                )}
              </button>

              {/* Skip forward 10s */}
              <button className="icon-btn" onClick={(e) => { e.stopPropagation(); handleSkip(10); }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13 7V17L18.5 12L13 7ZM11 17V7L5.5 12L11 17Z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* ✅ Ad banner inside the single return, at the bottom */}
      <ins className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client="ca-pub-XXXXXXXX"
        data-ad-slot="XXXXXXXX"
        data-ad-format="auto">
      </ins>
    </div>
  );
};

export default PlayerPage;